"use client"

import { useState, useCallback, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import Image from "next/image"
import { useLocale, useTranslations } from "next-intl"
import { usePathname } from "@/lib/i18n/navigation"
import { X } from "lucide-react"
import { findLegalKey, getLegalSlug, type Locale } from "@/lib/legal/slugs"

const locales = ["en", "de", "fr"] as const

const localeFlags: Record<string, string> = {
  en: "/images/flags/gb.svg",
  de: "/images/flags/de.svg",
  fr: "/images/flags/fr.svg",
}

/** Dark frame variables — matches NaturStore header/menu */
const darkFrameVars = {
  "--background": "var(--ns-color-dark)",
  "--foreground": "var(--ns-color-light)",
  "--muted": "var(--ns-color-mid)",
  "--muted-foreground": "color-mix(in srgb, var(--ns-color-light) 70%, transparent)",
  "--border": "var(--ns-color-mid)",
} as React.CSSProperties

/**
 * NaturStore language switcher — centered dark popup overlay
 * with serif title, dividers, and active state highlight.
 */
interface LanguageSwitcherProps {
  /** When provided, renders a text link instead of the flag circle */
  label?: string
}

export default function LanguageSwitcher({ label }: LanguageSwitcherProps = {}) {
  const locale = useLocale()
  const pathname = usePathname()
  const t = useTranslations("navigation")
  const [isOpen, setIsOpen] = useState(false)
  const [selectedLocale, setSelectedLocale] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const toggle = useCallback(() => setIsOpen((prev) => !prev), [])
  const close = useCallback(() => {
    setIsOpen(false)
    setSelectedLocale(null)
  }, [])

  // Focus trap + initial focus + Escape + restore focus on close
  useEffect(() => {
    if (!isOpen) return

    // Set initial focus on the close button inside the dialog
    const dialog = dialogRef.current
    if (dialog) {
      const closeBtn = dialog.querySelector<HTMLButtonElement>('[aria-label="Close"]')
      closeBtn?.focus()
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setIsOpen(false)
        return
      }
      // Focus trap: cycle Tab within dialog
      if (e.key === "Tab" && dialog) {
        const focusable = dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        )
        if (focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener("keydown", onKeyDown)
    const triggerEl = triggerRef.current
    return () => {
      document.removeEventListener("keydown", onKeyDown)
      // Restore focus to trigger on close
      triggerEl?.focus()
    }
  }, [isOpen])

  function switchLocale(newLocale: string) {
    if (newLocale === locale) {
      setIsOpen(false)
      return
    }
    setSelectedLocale(newLocale)

    // Legal pages have per-locale slugs, so swapping just the locale prefix
    // would land the user on the not-found page (e.g. /fr/legal/mentions-legales
    // → /de/legal/mentions-legales, which `isValidLegalSlug` in
    // src/lib/legal/slugs.ts rejects and the legal route turns into notFound()).
    // Translate the slug to the equivalent in the target locale instead — the
    // same rewrite the shared switcher does in src/components/layout/LanguageSwitcher.tsx.
    const legalMatch = pathname.match(/^\/legal\/([^/]+)/)
    if (legalMatch) {
      const key = findLegalKey(legalMatch[1])
      if (key) {
        const newSlug = getLegalSlug(key, newLocale as Locale)
        const rewritten = pathname.replace(/^\/legal\/[^/]+/, `/legal/${newSlug}`)
        window.location.assign(`/${newLocale}${rewritten}`)
        return
      }
    }

    window.location.assign(`/${newLocale}${pathname}`)
  }

  return (
    <>
      {label ? (
        <button
          ref={triggerRef}
          type="button"
          onClick={toggle}
          className="text-xs transition-colors duration-200 hover:text-[var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          aria-label={t("switchLanguage")}
          aria-expanded={isOpen}
        >
          {label}
        </button>
      ) : (
        <button
          ref={triggerRef}
          type="button"
          onClick={toggle}
          className="inline-flex items-center justify-center min-h-11 min-w-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
          aria-label={t("switchLanguage")}
          aria-expanded={isOpen}
        >
          <span
            className="block size-[22px] rounded-full ring-1 ring-border bg-[length:130%] bg-center"
            style={{ backgroundImage: `url(${localeFlags[locale]})` }}
            aria-hidden="true"
          />
        </button>
      )}

      {isOpen &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center">
            {/* Backdrop */}
            <button
              type="button"
              className="absolute inset-0 bg-black/60 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-300 cursor-default"
              onClick={close}
              aria-label="Close"
              tabIndex={-1}
            />

            {/* Popup */}
            <div
              ref={dialogRef}
              className="relative z-10 w-[300px] overflow-hidden rounded-xl shadow-2xl motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:duration-300 bg-[var(--background)] text-[var(--foreground)]"
              style={darkFrameVars}
              role="dialog"
              aria-modal="true"
              aria-label={t("switchLanguage")}
            >
              {/* Header — title left, close icon right */}
              <div className="flex items-center justify-between px-6 pt-6 pb-5 border-b border-[var(--border)]">
                <p
                  className="text-2xl"
                  style={{
                    fontFamily: "var(--ns-font-heading)",
                    fontWeight: 400,
                    letterSpacing: "0.03em",
                  }}
                >
                  {t("switchLanguage")}
                </p>
                <button
                  type="button"
                  onClick={close}
                  className="flex items-center justify-center min-h-11 min-w-11 -mr-2 text-[var(--muted-foreground)] transition-colors duration-200 hover:text-[var(--foreground)]"
                  aria-label="Close"
                >
                  <X className="size-5" />
                </button>
              </div>

              {/* Language options */}
              <div className="px-3 py-3">
                {locales.map((loc, index) => {
                  const isActive = loc === locale
                  const isLoading = selectedLocale === loc
                  return (
                    <div key={loc}>
                      <button
                        type="button"
                        onClick={() => switchLocale(loc)}
                        className={`flex w-full items-center gap-4 rounded-lg px-4 py-3.5 text-sm transition-[color,background-color] duration-200 ${
                          isActive || isLoading
                            ? "bg-[var(--muted)] text-[var(--foreground)]"
                            : "text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:bg-[var(--muted)]/50"
                        }`}
                      >
                        <Image
                          src={localeFlags[loc]}
                          alt=""
                          width={24}
                          height={18}
                          className="shadow-sm"
                          aria-hidden="true"
                        />
                        <span className="flex-1 text-left tracking-wide">{t(`locale.${loc}`)}</span>
                        {isLoading ? (
                          <span
                            className="size-4 rounded-full border-2 border-[var(--muted-foreground)] border-t-transparent motion-safe:animate-spin"
                            aria-hidden="true"
                          />
                        ) : isActive ? (
                          <span
                            className="size-2 rounded-full bg-[var(--foreground)]"
                            aria-hidden="true"
                          />
                        ) : null}
                      </button>
                      {index < locales.length - 1 && (
                        <div className="mx-4 border-b border-[var(--border)]/30" />
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
